import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { Card } from '@sohanscript/ui/card';
import { Heading } from '@sohanscript/ui/heading';
import { type ComponentProps, useEffect, useState } from 'react';
import { Img } from '@/components/image';
import { TestimonialsCard } from '@/components/testimonials-card';
import { api } from '@/lib/trpc/client';
import type { RouterOutputs } from '@/server/root';

type TestimonialItem = NonNullable<
	RouterOutputs['content']['testimonials']
>[number];

type TestimonialsProps = ComponentProps<'section'>;

function TestimonialStreamColumn({
	items,
	className,
	...props
}: ComponentProps<'div'> & { items: TestimonialItem[] }) {
	const duplicatedItems = items.concat(items);

	return (
		<div className={cn('min-h-0 overflow-hidden', className)} {...props}>
			<div className="testimonial-stream max-lg:animation-duration-[32s] hover:paused flex flex-col gap-4">
				{duplicatedItems.map((item, index) => (
					<TestimonialsCard
						key={`${item.name}-${index < items.length ? 'primary' : 'duplicate'}`}
						className="shrink-0"
						message={item.message}
						name={item.name}
						designation={item.designation}
						image={item.image}
					/>
				))}
			</div>
		</div>
	);
}

export function Testimonials({ className, ...props }: TestimonialsProps) {
	const [testimonials] = api.content.testimonials.useSuspenseQuery();
	const [featuredIndex, setFeaturedIndex] = useState(0);

	useEffect(() => {
		if (testimonials.length > 1) {
			setFeaturedIndex(Math.floor(Math.random() * testimonials.length));
		}
	}, [testimonials.length]);

	const featured = testimonials[featuredIndex] ?? testimonials[0];
	const wallItems = testimonials.filter((_, index) => index !== featuredIndex);
	const wallDisplayItems = wallItems.length > 0 ? wallItems : testimonials;
	const mobileItems = wallDisplayItems;
	const firstColumn = wallDisplayItems.filter((_, index) => index % 2 === 0);
	const secondColumn = wallDisplayItems.filter((_, index) => index % 2 === 1);

	return (
		<section
			className={cn('container w-full py-12 lg:py-20', className)}
			aria-labelledby="testimonials-title"
			{...props}
		>
			<Heading
				className="mb-6 lg:mb-16"
				titlePrefix={m.testimonials_heading_label()}
				ampersand
				titleHighlight={m.heading_testimonials_highlight()}
				description={m.testimonials_heading_tagline()}
			/>

			{featured ? (
				<div className="grid gap-6 lg:grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)] lg:items-start lg:gap-8">
					<Card className="relative min-h-[22rem] border-primary/20 bg-background-light p-4 shadow-card lg:sticky lg:top-[calc(var(--height-navbar)+2rem)] lg:min-h-0 lg:p-6">
						<div className="relative">
							<div className="flex items-center justify-between gap-3">
								<span className="font-mono text-2xs text-primary uppercase tracking-[0.12em]">
									featured client
								</span>
								<span className="inline-flex items-center gap-1 text-2xs text-primary">
									<Iconify
										icon="lucide:star"
										className="size-3 fill-current"
										aria-hidden
									/>
									5.0
								</span>
							</div>
							<Iconify
								icon="lucide:quote"
								className="mt-5 size-7 text-primary/35 lg:mt-8"
								aria-hidden
							/>
							<blockquote className="mt-3 max-w-[36ch] font-souvenir text-foreground-light text-lg leading-relaxed sm:text-xl">
								{featured.message}
							</blockquote>
							<div className="mt-5 flex items-center gap-3 border-border/60 border-t pt-4 lg:mt-7">
								<div className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-primary/30 bg-primary/10 font-mono text-primary text-xs">
									<Img
										className="size-full object-cover"
										height={44}
										sizes="44px"
										src={featured.image}
										width={44}
										alt=""
									/>
								</div>
								<div className="min-w-0">
									<p className="truncate font-semibold text-foreground-light">
										{featured.name}
									</p>
									<p className="truncate text-muted-foreground text-xs">
										{featured.designation}
									</p>
								</div>
								<span className="ms-auto inline-flex shrink-0 items-center gap-1 text-2xs text-success">
									<Iconify
										icon="lucide:badge-check"
										className="size-3"
										aria-hidden
									/>
									verified
								</span>
							</div>
						</div>
					</Card>

					<div className="testimonial-wall-mask relative h-[28rem] overflow-hidden lg:h-[38rem]">
						<div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-16 bg-linear-to-b from-background to-transparent" />
						<div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 bg-linear-to-t from-background to-transparent" />
						<div className="grid h-full grid-cols-1 gap-4 lg:grid-cols-2">
							<TestimonialStreamColumn
								items={mobileItems}
								className="lg:hidden"
							/>
							<TestimonialStreamColumn
								items={firstColumn}
								className="hidden lg:block"
							/>
							<TestimonialStreamColumn
								items={secondColumn}
								className="hidden lg:block"
							/>
						</div>
					</div>
				</div>
			) : null}
		</section>
	);
}
